import React, { useState, useMemo } from "react";
import TripDriverAssignment from "../components/TripDriverAssignment";
import {
  Search,
  Eye,
  X,
  Check,
  CalendarDays,
  Clock,
  Users,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  SlidersHorizontal,
  Hash,
  MessageSquare,
  Loader2,
  Send,
  ShoppingCart,
  FileText,
} from "lucide-react";
import {
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
  useGetCustomBookingsQuery,
  useAdminRespondCustomBookingMutation,
} from "../store/api/adminApi";
import { useToast } from "../components/Toast";

const PAGE_SIZE = 10;

const STATUSES = ["All", "Pending", "Accepted", "Declined", "Completed", "Cancelled"];

const STATUS_STYLE = {
  pending:       { pill: "bg-amber-50 text-amber-500",   dot: "bg-amber-400",  label: "Pending" },
  accepted:      { pill: "bg-green-50 text-green-600",   dot: "bg-green-500",  label: "Accepted" },
  declined:      { pill: "bg-red-50 text-primary",       dot: "bg-primary",    label: "Declined" },
  completed:     { pill: "bg-blue-50 text-blue-600",     dot: "bg-blue-400",   label: "Completed" },
  cancelled:     { pill: "bg-gray-100 text-gray-500",    dot: "bg-gray-400",   label: "Cancelled" },
  price_offered: { pill: "bg-purple-50 text-purple-600", dot: "bg-purple-400", label: "Price Offered" },
  user_countered:{ pill: "bg-orange-50 text-orange-500", dot: "bg-orange-400", label: "Counter Offer" },
  agreed:        { pill: "bg-teal-50 text-teal-600",     dot: "bg-teal-400",   label: "Agreed" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-manrope-semibold ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function Avatar({ name = "?", size = 36 }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <span className="font-manrope-bold text-primary" style={{ fontSize: size * 0.36 }}>{initials}</span>
    </div>
  );
}

function FilterDropdown({ active, onChange }) {
  const [open, setOpen] = useState(false);
  const activeCount = active !== "All" ? 1 : 0;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-[13px] font-manrope-semibold transition-all ${
          active !== "All" ? "border-primary bg-primary/5 text-primary" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
        }`}
      >
        <SlidersHorizontal size={14} strokeWidth={1.8} />
        Filter
        {activeCount > 0 && (
          <span className="bg-primary text-white text-[10px] font-manrope-bold w-4 h-4 rounded-full flex items-center justify-center">{activeCount}</span>
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg z-20 w-[170px] overflow-hidden">
          <p className="text-[10px] font-manrope-semibold text-gray-400 uppercase tracking-wider px-4 pt-3 pb-1">Status</p>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => { onChange(s); setOpen(false); }}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition-all hover:bg-gray-50 ${
                active === s ? "text-primary font-manrope-semibold" : "text-gray-600 font-manrope-regular"
              }`}
            >
              {s}
              {active === s && <Check size={13} className="text-primary" strokeWidth={2.2} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeclineModal({ order, onClose, onConfirm, isLoading }) {
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[420px] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-manrope-bold text-gray-900">Decline order</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-3">
          Booking <span className="font-manrope-semibold text-gray-700">{order?.bookingNumber}</span>
        </p>
        <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-2">Reason for declining (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={5}
          placeholder="Explain why this order is being declined..."
          className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[12px] font-manrope-regular text-gray-600 focus:outline-none focus:border-primary transition-all resize-none mb-5"
        />
        <div className="flex gap-3">
          <button onClick={onClose} disabled={isLoading} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(note)} disabled={isLoading} className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
            <X size={14} strokeWidth={2.2} />
            {isLoading ? "Declining…" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AcceptedModal({ onDone }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onDone} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[300px] p-8 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-4">
          <Check size={30} className="text-white" strokeWidth={2.5} />
        </div>
        <p className="text-[15px] font-manrope-bold text-green-500 mb-5">Order accepted</p>
        <button onClick={onDone} className="w-full py-2.5 rounded-xl bg-green-500 text-white text-[13px] font-manrope-semibold hover:bg-green-600 transition-all">Done</button>
      </div>
    </div>
  );
}

function OrderDetailsPanel({ order, onClose, onDecline, onAccept, isUpdating }) {
  const dateStr = order.date
    ? new Date(order.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";
  const isPending = order.status === "pending";

  return (
    <div className="w-[300px] bg-white border-l border-gray-100 flex flex-col fixed right-0 top-0 h-full z-40 shadow-xl overflow-y-auto">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <h2 className="text-[15px] font-manrope-bold text-gray-900">Order details</h2>
          <StatusBadge status={order.status} />
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
          <X size={15} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 px-5 py-5 flex flex-col gap-5">
        <TripDriverAssignment order={order} />
        <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5">
          <Hash size={13} className="text-gray-400" strokeWidth={1.8} />
          <span className="text-[12px] font-manrope-bold text-gray-700">{order.bookingNumber}</span>
        </div>

        <div>
          <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Customer</p>
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <Avatar name={order.customer?.name} size={36} />
            <div className="min-w-0">
              <p className="text-[13px] font-manrope-bold text-gray-900 truncate">{order.customer?.name || "—"}</p>
              <p className="text-[11px] font-manrope-regular text-gray-400 truncate">{order.customer?.email || "—"}</p>
              {order.customer?.phone && <p className="text-[11px] font-manrope-regular text-gray-400">{order.customer.phone}</p>}
            </div>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Service</p>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-[13px] font-manrope-bold text-gray-900">{order.service?.title || "—"}</p>
            {order.service?.category && <p className="text-[11px] font-manrope-regular text-gray-400 mt-0.5">{order.service.category}</p>}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Scheduled</p>
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
              <CalendarDays size={15} className="text-gray-500" strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[13px] font-manrope-bold text-gray-900">{dateStr}</p>
              <p className="text-[11px] font-manrope-regular text-gray-400">{order.timeSlot || "—"}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-1 bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Users size={12} className="text-gray-400" strokeWidth={1.6} />
              <span className="text-[10px] font-manrope-regular text-gray-400">Guests</span>
            </div>
            <p className="text-[18px] font-manrope-bold text-gray-900">{order.quantity ?? "—"}</p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign size={12} className="text-gray-400" strokeWidth={1.6} />
              <span className="text-[10px] font-manrope-regular text-gray-400">Total paid</span>
            </div>
            <p className="text-[18px] font-manrope-bold text-gray-900">${order.subtotal != null ? Number(order.subtotal).toFixed(2) : "—"}</p>
          </div>
        </div>

        {order.note && (
          <div className="bg-amber-50 rounded-xl px-4 py-3">
            <p className="text-[10px] font-manrope-semibold text-amber-500 uppercase tracking-wide mb-1">Note</p>
            <p className="text-[12px] font-manrope-regular text-gray-600 leading-relaxed">{order.note}</p>
          </div>
        )}

        {isPending && (
          <div className="flex gap-2 mt-auto pt-2">
            <button onClick={onDecline} disabled={isUpdating} className="flex-1 py-2.5 rounded-xl bg-primary/10 text-primary text-[12px] font-manrope-semibold hover:bg-primary/20 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
              <X size={13} strokeWidth={2.2} />
              Decline
            </button>
            <button onClick={onAccept} disabled={isUpdating} className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-[12px] font-manrope-semibold hover:bg-green-600 transition-all disabled:opacity-60 flex items-center justify-center gap-1.5">
              <Check size={13} strokeWidth={2.2} />
              {isUpdating ? "Saving…" : "Accept"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Custom Requests section
───────────────────────────────────────────────────────── */

const CB_STATUSES = ["All", "Pending", "Price Offered", "Counter Offer", "Agreed", "Declined"];
const CB_STATUS_MAP = {
  "All": "All",
  "Pending": "pending",
  "Price Offered": "price_offered",
  "Counter Offer": "user_countered",
  "Agreed": "agreed",
  "Declined": "declined",
};

function PriceOfferModal({ booking, action, onClose, onConfirm, isLoading }) {
  const isCounter = action === "counter";
  const [price, setPrice] = useState(booking.currentPrice ? String(booking.currentPrice) : "");
  const [message, setMessage] = useState("");
  const [err, setErr] = useState("");

  const handle = () => {
    if (!price || isNaN(Number(price)) || Number(price) <= 0) { setErr("Enter a valid price."); return; }
    onConfirm({ action: isCounter ? "counter" : "offer_price", price: Number(price), message });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[400px] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-manrope-bold text-gray-900">{isCounter ? "Counter offer" : "Send price offer"}</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>
        <div className="mb-3">
          <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">Price (USD)</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-manrope-semibold text-gray-400">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => { setPrice(e.target.value); setErr(""); }}
              className="w-full pl-7 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 focus:outline-none focus:border-primary transition-all"
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">Message (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Add a note to the customer..."
            className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[12px] font-manrope-regular text-gray-600 focus:outline-none focus:border-primary transition-all resize-none"
          />
        </div>
        {err && <p className="text-[12px] text-red-500 bg-red-50 px-4 py-2 rounded-xl mb-3 font-manrope-regular">{err}</p>}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
          <button onClick={handle} disabled={isLoading} className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
            {isLoading && <Loader2 size={13} className="animate-spin" />}
            <Send size={13} strokeWidth={2} />
            {isLoading ? "Sending…" : "Send offer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NegotiationTimeline({ negotiations = [] }) {
  if (!negotiations.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {negotiations.map((n, i) => {
        const isAdmin = n.by === "admin";
        return (
          <div key={i} className={`rounded-xl px-4 py-3 ${isAdmin ? "bg-primary/5 border border-primary/10" : "bg-gray-50"}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[10px] font-manrope-semibold uppercase tracking-wide ${isAdmin ? "text-primary" : "text-gray-400"}`}>
                {isAdmin ? "You" : "Customer"} · {n.action}
              </span>
              {n.price != null && (
                <span className="text-[12px] font-manrope-bold text-gray-700">${Number(n.price).toFixed(2)}</span>
              )}
            </div>
            {n.message && <p className="text-[12px] font-manrope-regular text-gray-600 leading-relaxed">{n.message}</p>}
            <p className="text-[10px] font-manrope-regular text-gray-400 mt-1">{new Date(n.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
          </div>
        );
      })}
    </div>
  );
}

function CustomBookingDetailsPanel({ booking, onClose, onAction, isActing }) {
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceAction, setPriceAction] = useState("offer_price");
  const toast = useToast();

  const dateStr = booking.preferredDate
    ? new Date(booking.preferredDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  const canAct = ["pending", "user_countered"].includes(booking.status);
  const canAccept = booking.status === "user_countered" || (booking.status === "pending" && booking.currentPrice);

  const handleDecline = async () => {
    try {
      await onAction({ id: booking.id, action: "decline" });
    } catch { /* handled by caller */ }
  };

  const handleAccept = async () => {
    try {
      await onAction({ id: booking.id, action: "accept" });
    } catch { /* handled by caller */ }
  };

  const openOffer = (act) => { setPriceAction(act); setShowPriceModal(true); };

  const handlePriceConfirm = async (body) => {
    try {
      await onAction({ id: booking.id, ...body });
      setShowPriceModal(false);
      toast.success("Offer sent to customer.");
    } catch { /* handled by caller */ }
  };

  return (
    <>
      <div className="w-[340px] bg-white border-l border-gray-100 flex flex-col fixed right-0 top-0 h-full z-40 shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <h2 className="text-[14px] font-manrope-bold text-gray-900">Custom request</h2>
            <StatusBadge status={booking.status} />
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 px-5 py-5 flex flex-col gap-4 overflow-y-auto">
          {/* Booking number */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5">
            <Hash size={13} className="text-gray-400" strokeWidth={1.8} />
            <span className="text-[12px] font-manrope-bold text-gray-700">{booking.bookingNumber}</span>
          </div>

          {/* Customer */}
          <div>
            <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Customer</p>
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
              <Avatar name={booking.customerName} size={36} />
              <div className="min-w-0">
                <p className="text-[13px] font-manrope-bold text-gray-900 truncate">{booking.customerName || "—"}</p>
                <p className="text-[11px] font-manrope-regular text-gray-400 truncate">{booking.customerEmail || "—"}</p>
                {booking.customerPhone && <p className="text-[11px] font-manrope-regular text-gray-400">{booking.customerPhone}</p>}
              </div>
            </div>
          </div>

          {/* Date & time */}
          <div>
            <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Requested schedule</p>
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                <CalendarDays size={15} className="text-gray-500" strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-[13px] font-manrope-bold text-gray-900">{dateStr}</p>
                <p className="text-[11px] font-manrope-regular text-gray-400">
                  {booking.preferredTimeSlot || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Description</p>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-[12px] font-manrope-regular text-gray-700 leading-relaxed">{booking.serviceDescription || "—"}</p>
            </div>
          </div>

          {/* Current price if set */}
          {booking.currentPrice != null && (
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-xl px-4 py-3">
              <DollarSign size={14} className="text-primary" strokeWidth={1.6} />
              <div>
                <p className="text-[10px] font-manrope-semibold text-primary uppercase tracking-wide">Current price</p>
                <p className="text-[16px] font-manrope-bold text-gray-900">${Number(booking.currentPrice).toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Negotiation history */}
          {booking.negotiations?.length > 0 && (
            <div>
              <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">Negotiation history</p>
              <NegotiationTimeline negotiations={booking.negotiations} />
            </div>
          )}

          {/* Actions */}
          {canAct && (
            <div className="flex flex-col gap-2 mt-auto pt-2">
              <button
                onClick={() => openOffer(booking.status === "user_countered" ? "counter" : "offer_price")}
                disabled={isActing}
                className="w-full py-2.5 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                <Send size={13} strokeWidth={2} />
                {booking.status === "user_countered" ? "Counter offer" : "Send price offer"}
              </button>
              {canAccept && (
                <button
                  onClick={handleAccept}
                  disabled={isActing}
                  className="w-full py-2.5 rounded-xl bg-green-500 text-white text-[12px] font-manrope-semibold hover:bg-green-600 transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
                >
                  <Check size={13} strokeWidth={2.2} />
                  {isActing ? "Saving…" : "Accept & agree"}
                </button>
              )}
              <button
                onClick={handleDecline}
                disabled={isActing}
                className="w-full py-2.5 rounded-xl bg-primary/10 text-primary text-[12px] font-manrope-semibold hover:bg-primary/20 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <X size={13} strokeWidth={2.2} />
                Decline request
              </button>
            </div>
          )}

          {booking.status === "price_offered" && (
            <div className="mt-auto pt-2 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-center">
              <p className="text-[12px] font-manrope-semibold text-purple-600">Awaiting customer response</p>
              <p className="text-[11px] font-manrope-regular text-purple-400 mt-0.5">Customer has been notified of the price offer.</p>
            </div>
          )}

          {booking.status === "agreed" && (
            <div className="mt-auto pt-2 bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 text-center">
              <p className="text-[12px] font-manrope-semibold text-teal-600">Price agreed</p>
              <p className="text-[11px] font-manrope-regular text-teal-400 mt-0.5">Both parties agreed on ${Number(booking.currentPrice || 0).toFixed(2)}.</p>
            </div>
          )}

          {booking.status === "declined" && (
            <div className="mt-auto pt-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-center">
              <p className="text-[12px] font-manrope-semibold text-primary">Request declined</p>
            </div>
          )}
        </div>
      </div>

      {showPriceModal && (
        <PriceOfferModal
          booking={booking}
          action={priceAction}
          onClose={() => setShowPriceModal(false)}
          onConfirm={handlePriceConfirm}
          isLoading={isActing}
        />
      )}
    </>
  );
}

function CustomRequests() {
  const [search, setSearch] = useState("");
  const [liveSearch, setLiveSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selected, setSelected] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();

  const { data, isLoading, isError } = useGetCustomBookingsQuery();
  const [adminRespond, { isLoading: isActing }] = useAdminRespondCustomBookingMutation();

  const filtered = useMemo(() => {
    const bookings = data?.data || [];
    const q = liveSearch.toLowerCase();
    const statusVal = CB_STATUS_MAP[filterStatus];
    return bookings.filter((b) => {
      const matchSearch = !q ||
        b.customerName?.toLowerCase().includes(q) ||
        b.customerEmail?.toLowerCase().includes(q) ||
        b.bookingNumber?.toLowerCase().includes(q) ||
        b.serviceDescription?.toLowerCase().includes(q);
      const matchStatus = statusVal === "All" || b.status === statusVal;
      return matchSearch && matchStatus;
    });
  }, [data?.data, liveSearch, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage, "...", totalPages];
  }, [totalPages, currentPage]);

  const handleAction = async (body) => {
    const { id, ...rest } = body;
    try {
      await adminRespond({ id, ...rest }).unwrap();
      if (rest.action === "decline") {
        toast.success("Request declined.");
        setSelected(null);
      } else if (rest.action === "accept") {
        toast.success("Booking agreed.");
      }
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to update request.");
      throw err;
    }
  };

  return (
    <>
      <div className={`transition-all ${selected ? "mr-[340px]" : ""}`}>
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-xs">
            <input
              type="text"
              placeholder="Search by name, email, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setLiveSearch(search); setCurrentPage(1); } }}
              className="w-full pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
            />
          </div>
          <button
            onClick={() => { setLiveSearch(search); setCurrentPage(1); }}
            className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:opacity-90 transition-all flex-shrink-0"
          >
            <Search size={16} className="text-white" strokeWidth={2} />
          </button>
          <div className="ml-auto">
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                className="pl-4 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-[13px] font-manrope-semibold text-gray-600 focus:outline-none focus:border-primary transition-all appearance-none"
              >
                {CB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <SlidersHorizontal size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" strokeWidth={1.8} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[2fr_1.5fr_1.5fr_2fr_1fr_1fr] px-5 py-3 border-b border-gray-50">
            {["Customer", "Date", "Time Slot", "Description", "Status", "Action"].map((col) => (
              <span key={col} className="text-[12px] font-manrope-semibold text-gray-400">{col}</span>
            ))}
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-[13px] font-manrope-regular">Loading…</span>
            </div>
          )}
          {isError && (
            <div className="flex items-center justify-center py-16">
              <p className="text-[13px] font-manrope-regular text-gray-400">Failed to load custom requests.</p>
            </div>
          )}
          {!isLoading && !isError && filtered.length === 0 && (
            <div className="py-16 flex flex-col items-center gap-2">
              <FileText size={32} className="text-gray-200" strokeWidth={1.2} />
              <p className="text-[13px] font-manrope-semibold text-gray-400">No custom requests found</p>
            </div>
          )}

          {!isLoading && !isError && paginated.map((b) => {
            const dateStr = b.preferredDate
              ? new Date(b.preferredDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "—";
            const slotStr = b.preferredTimeSlot || "—";
            return (
              <div key={b.id} className="grid grid-cols-[2fr_1.5fr_1.5fr_2fr_1fr_1fr] px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center">
                <div className="flex items-center gap-2.5">
                  <Avatar name={b.customerName} size={32} />
                  <div className="min-w-0">
                    <p className="text-[13px] font-manrope-semibold text-gray-800 truncate">{b.customerName || "—"}</p>
                    <p className="text-[11px] font-manrope-regular text-gray-400 truncate">{b.customerEmail || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <CalendarDays size={11} className="text-gray-400" strokeWidth={1.6} />
                  <span className="text-[12px] font-manrope-regular text-gray-500">{dateStr}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={11} className="text-gray-400" strokeWidth={1.6} />
                  <span className="text-[12px] font-manrope-regular text-gray-500">{slotStr}</span>
                </div>
                <p className="text-[12px] font-manrope-regular text-gray-500 truncate pr-2">{b.serviceDescription || "—"}</p>
                <StatusBadge status={b.status} />
                <div>
                  <button
                    onClick={() => setSelected(b)}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Eye size={14} className="text-gray-500" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            );
          })}

          {!isLoading && !isError && (
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-[12px] font-manrope-regular text-gray-500">
                Total <span className="font-manrope-bold text-gray-800">{filtered.length}</span> requests
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronsLeft size={14} /></button>
                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronLeft size={14} /></button>
                {pageNumbers.map((p, i) =>
                  p === "..." ? (
                    <span key={`e-${i}`} className="w-7 h-7 flex items-center justify-center text-[12px] text-gray-400">…</span>
                  ) : (
                    <button key={p} onClick={() => setCurrentPage(p)} className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${currentPage === p ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"}`}>{p}</button>
                  )
                )}
                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronRight size={14} /></button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronsRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <CustomBookingDetailsPanel
          booking={selected}
          onClose={() => setSelected(null)}
          onAction={handleAction}
          isActing={isActing}
        />
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   Main Orders page with tabs
───────────────────────────────────────────────────────── */

export default function Orders() {
  const [tab, setTab] = useState("orders");
  const [search, setSearch] = useState("");
  const [liveSearch, setLiveSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDecline, setShowDecline] = useState(false);
  const [showAccepted, setShowAccepted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isError } = useGetOrdersQuery();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const toast = useToast();

  const filtered = useMemo(() => {
    const orders = data?.data ?? [];
    const q = liveSearch.toLowerCase();
    return orders.filter((o) => {
      const matchSearch = !q ||
        o.customer?.name?.toLowerCase().includes(q) ||
        o.customer?.email?.toLowerCase().includes(q) ||
        o.service?.title?.toLowerCase().includes(q) ||
        o.bookingNumber?.toLowerCase().includes(q);
      const matchStatus = filterStatus === "All" || o.status === filterStatus.toLowerCase();
      return matchSearch && matchStatus;
    });
  }, [data?.data, liveSearch, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage, "...", totalPages];
  }, [totalPages, currentPage]);

  const handleAccept = async () => {
    if (!selectedOrder) return;
    try {
      await updateStatus({ id: selectedOrder.id, status: "accepted" }).unwrap();
      setShowAccepted(true);
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to accept order.");
    }
  };

  const handleDeclineConfirm = async (note) => {
    if (!selectedOrder) return;
    try {
      await updateStatus({ id: selectedOrder.id, status: "declined", note }).unwrap();
      setShowDecline(false);
      setSelectedOrder(null);
      toast.success("Order declined.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to decline order.");
    }
  };

  return (
    <>
      <div className={`flex flex-col min-h-screen transition-all ${selectedOrder ? "mr-[300px]" : ""}`}>
        {/* Header */}
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">Order management</h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">Manage all bookings and update their status from here.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-gray-600">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-manrope-bold text-primary">V</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">Vigelanto</span>
                <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">Admin</span>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 px-8 pb-8">
          {/* Tabs */}
          <div className="flex items-center gap-1 mb-5 bg-white rounded-xl p-1 w-fit border border-gray-100">
            <button
              onClick={() => setTab("orders")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-manrope-semibold transition-all ${tab === "orders" ? "bg-primary text-white" : "text-gray-500 hover:text-gray-800"}`}
            >
              <ShoppingCart size={14} strokeWidth={1.8} />
              Regular orders
            </button>
            <button
              onClick={() => setTab("custom")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-manrope-semibold transition-all ${tab === "custom" ? "bg-primary text-white" : "text-gray-500 hover:text-gray-800"}`}
            >
              <MessageSquare size={14} strokeWidth={1.8} />
              Custom requests
            </button>
          </div>

          {tab === "orders" && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-3 mb-5">
                <div className="relative flex-1 max-w-xs">
                  <input
                    type="text"
                    placeholder="Search by name, email, service…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { setLiveSearch(search); setCurrentPage(1); } }}
                    className="w-full pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                  />
                </div>
                <button
                  onClick={() => { setLiveSearch(search); setCurrentPage(1); }}
                  className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:opacity-90 transition-all flex-shrink-0"
                >
                  <Search size={16} className="text-white" strokeWidth={2} />
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <FilterDropdown active={filterStatus} onChange={(s) => { setFilterStatus(s); setCurrentPage(1); }} />
                  {filterStatus !== "All" && (
                    <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-xl px-3 py-2">
                      <StatusBadge status={filterStatus.toLowerCase()} />
                      <button onClick={() => { setFilterStatus("All"); setCurrentPage(1); }} className="w-4 h-4 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all ml-1">
                        <X size={10} className="text-gray-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-2xl overflow-hidden">
                <div className="grid grid-cols-[2fr_2fr_2fr_1fr_1.3fr_1fr_1fr] px-5 py-3 border-b border-gray-50">
                  {["Customer", "Email", "Service", "Price", "Date", "Status", "Action"].map((col) => (
                    <span key={col} className="text-[12px] font-manrope-semibold text-gray-400">{col}</span>
                  ))}
                </div>

                {isLoading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="flex items-center justify-center py-16">
                    <p className="text-[13px] font-manrope-regular text-gray-400">Failed to load orders.</p>
                  </div>
                )}
                {!isLoading && !isError && filtered.length === 0 && (
                  <div className="py-16 flex flex-col items-center gap-2">
                    <p className="text-[13px] font-manrope-semibold text-gray-400">No orders found</p>
                    <p className="text-[11px] font-manrope-regular text-gray-300">Try adjusting your search or filter</p>
                  </div>
                )}

                {!isLoading && !isError && paginated.map((order) => (
                  <div key={order.id} className="grid grid-cols-[2fr_2fr_2fr_1fr_1.3fr_1fr_1fr] px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={order.customer?.name} size={32} />
                      <span className="text-[13px] font-manrope-semibold text-gray-800 truncate">{order.customer?.name || "—"}</span>
                    </div>
                    <span className="text-[12px] font-manrope-regular text-gray-500 truncate">{order.customer?.email || "—"}</span>
                    <span className="text-[12px] font-manrope-regular text-gray-500 truncate">{order.service?.title || "—"}</span>
                    <span className="text-[12px] font-manrope-semibold text-gray-700">${order.subtotal != null ? Number(order.subtotal).toFixed(2) : "—"}</span>
                    <div className="flex items-center gap-1.5">
                      <Clock size={11} className="text-gray-400" strokeWidth={1.6} />
                      <span className="text-[12px] font-manrope-regular text-gray-500">
                        {order.date ? new Date(order.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </span>
                    </div>
                    <StatusBadge status={order.status} />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                      >
                        <Eye size={14} className="text-gray-500" strokeWidth={1.6} />
                      </button>
                    </div>
                  </div>
                ))}

                {!isLoading && !isError && (
                  <div className="flex items-center justify-between px-5 py-4">
                    <span className="text-[12px] font-manrope-regular text-gray-500">
                      Total <span className="font-manrope-bold text-gray-800">{filtered.length}</span> orders
                      {filterStatus !== "All" && <span className="ml-1 text-gray-400">(filtered)</span>}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronsLeft size={14} /></button>
                      <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronLeft size={14} /></button>
                      {pageNumbers.map((p, i) =>
                        p === "..." ? (
                          <span key={`e-${i}`} className="w-7 h-7 flex items-center justify-center text-[12px] text-gray-400">…</span>
                        ) : (
                          <button key={p} onClick={() => setCurrentPage(p)} className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${currentPage === p ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"}`}>{p}</button>
                        )
                      )}
                      <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronRight size={14} /></button>
                      <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"><ChevronsRight size={14} /></button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "custom" && <CustomRequests />}
        </div>
      </div>

      {selectedOrder && tab === "orders" && (
        <OrderDetailsPanel
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onDecline={() => setShowDecline(true)}
          onAccept={handleAccept}
          isUpdating={isUpdating}
        />
      )}
      {showDecline && (
        <DeclineModal
          order={selectedOrder}
          onClose={() => setShowDecline(false)}
          onConfirm={handleDeclineConfirm}
          isLoading={isUpdating}
        />
      )}
      {showAccepted && (
        <AcceptedModal onDone={() => { setShowAccepted(false); setSelectedOrder(null); }} />
      )}
    </>
  );
}
